import { useMemo, useCallback } from "react";
import { Account } from "../../../types";
import { WorkspaceMember } from "./useWorkspaceData";

interface UseFilterHelpersProps {
  searchQuery: string;
  typeFilter: string;
  groupFilter: string;
  categoryFilter: string;
  startDate: string;
  endDate: string;
  selectedUsers: string[];
  selectedAccountFilters: string[];
  showOnlyEdited: boolean;
  workspaceMembers: WorkspaceMember[];
  accounts: Account[];
  setSearchQuery: (value: string) => void;
  setTypeFilter: (value: string) => void;
  setGroupFilter: (value: string) => void;
  setCategoryFilter: (value: string) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setSelectedAccountFilters: (value: string[]) => void;
  setSelectedUsers: (value: string[]) => void;
  setShowOnlyEdited: (value: boolean) => void;
  /** When provided, clearAllFilters will call this instead of clearing dates to "" (resets to default Monthly) */
  onClearDatesToDefault?: () => void;
  /** For hasActiveFilters: when period is Monthly and offset 0 with current month, treat as default (not active) */
  selectedPeriod?: string;
  currentPeriodOffset?: number;
}

export const useFilterHelpers = ({
  searchQuery,
  typeFilter,
  groupFilter,
  categoryFilter,
  startDate,
  endDate,
  selectedUsers,
  selectedAccountFilters,
  showOnlyEdited,
  workspaceMembers,
  accounts,
  setSearchQuery,
  setTypeFilter,
  setGroupFilter,
  setCategoryFilter,
  setStartDate,
  setEndDate,
  setSelectedAccountFilters,
  setSelectedUsers,
  setShowOnlyEdited,
  onClearDatesToDefault,
  selectedPeriod = "Monthly",
  currentPeriodOffset = 0,
}: UseFilterHelpersProps) => {
  // Check if any filters are active (same logic as Analytics).
  // When there are no accounts (e.g. new workspace), treat as "all selected" so we don't show Clear all.
  const hasActiveFilters = useMemo(() => {
    const userFilterActive = selectedUsers.length > 0 && selectedUsers.length < workspaceMembers.length;
    const allAccountsSelected =
      accounts.length === 0 ||
      (selectedAccountFilters.length === 0 || selectedAccountFilters.length === accounts.length);

    // Default Monthly period: current month, offset 0 - not considered active
    // Treat empty dates as default too (before period effect initializes)
    const today = new Date();
    const defaultFrom = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultTo = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const defaultFromStr = `${defaultFrom.getFullYear()}-${String(defaultFrom.getMonth() + 1).padStart(2, "0")}-${String(defaultFrom.getDate()).padStart(2, "0")}`;
    const defaultToStr = `${defaultTo.getFullYear()}-${String(defaultTo.getMonth() + 1).padStart(2, "0")}-${String(defaultTo.getDate()).padStart(2, "0")}`;
    const datesMatchDefault = startDate === defaultFromStr && endDate === defaultToStr;
    const datesEmpty = startDate === "" && endDate === "";
    const periodOffsetIsDefault = currentPeriodOffset === 0;
    const dateFilterAtDefault = selectedPeriod === "Monthly" && periodOffsetIsDefault && (datesMatchDefault || datesEmpty);

    return (
      searchQuery !== "" ||
      typeFilter !== "All" ||
      groupFilter !== "All" ||
      categoryFilter !== "All" ||
      userFilterActive ||
      !allAccountsSelected ||
      !dateFilterAtDefault ||
      showOnlyEdited
    );
  }, [searchQuery, typeFilter, groupFilter, categoryFilter, selectedUsers, workspaceMembers, selectedAccountFilters, accounts, startDate, endDate, selectedPeriod, currentPeriodOffset, showOnlyEdited]);

  // Clear all filters (same as Analytics - reset to default Monthly)
  const clearAllFilters = useCallback((): void => {
    setSearchQuery("");
    setTypeFilter("All");
    setGroupFilter("All");
    setCategoryFilter("All");
    setSelectedAccountFilters([]);
    if (onClearDatesToDefault) {
      onClearDatesToDefault();
    } else {
      setStartDate("");
      setEndDate("");
    }
    setSelectedUsers([]);
    setShowOnlyEdited(false);
  }, [setSearchQuery, setTypeFilter, setGroupFilter, setCategoryFilter, setSelectedAccountFilters, setStartDate, setEndDate, setSelectedUsers, setShowOnlyEdited, onClearDatesToDefault]);

  // Clear individual filters
  const clearTypeFilter = useCallback((): void => setTypeFilter("All"), [setTypeFilter]);
  const clearGroupFilter = useCallback((): void => setGroupFilter("All"), [setGroupFilter]);
  const clearCategoryFilter = useCallback((): void => setCategoryFilter("All"), [setCategoryFilter]);
  const clearStartDate = useCallback((): void => setStartDate(""), [setStartDate]);
  const clearEndDate = useCallback((): void => setEndDate(""), [setEndDate]);
  const clearAccountFilter = useCallback((): void => setSelectedAccountFilters([]), [setSelectedAccountFilters]);
  const clearUserFilter = useCallback((): void => setSelectedUsers([]), [setSelectedUsers]);
  const clearEditedFilter = useCallback((): void => setShowOnlyEdited(false), [setShowOnlyEdited]);

  return {
    hasActiveFilters,
    clearAllFilters,
    clearTypeFilter,
    clearGroupFilter,
    clearCategoryFilter,
    clearStartDate,
    clearEndDate,
    clearAccountFilter,
    clearUserFilter,
    clearEditedFilter,
  };
};
